/**
 * Nordea brand style configuration — colors, typography, and prompt
 * rules extracted from the client's 2024 Corporate Finance V2 deck
 * (slide master colorScheme "Nordea 2024" + fontScheme "Nordea" / Arial,
 * layout placeholder geometry, via the OOXML).
 *
 * Mirrors the shape of `mediobancaStyleConfig.ts` / `paretoStyleConfig.ts`.
 * When Nordea style is active the prompt below is appended to the
 * system prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: theme colors come from the master-linked colorScheme
 * "Nordea 2024". The fontScheme uses Arial for both major and minor
 * faces (no embedded fonts). Slide size is 841.9×595.2 pt (A4 landscape).
 * The generic body layout is V2's "Two Content": 20pt bold navy title
 * and two 370.3pt body columns. Layout body defaults are ~14pt at
 * level 1 and ~10.5pt below, with effective navy text. "Create Layout 1"
 * is a specialized, placeholder-heavy layout (e.g. Strip profiles), not
 * the default body. That master's first layout is Title Slide, so
 * content slides must use contentLayoutName "Two Content" rather than
 * the cover — the generic name picker would otherwise choose
 * "4 up callout". A wordmark picture ("Picture 4") lives on slide
 * master 1 (bottom-right). "SecondaryLogo" is an empty hidden text box,
 * not a second logo. Template charts (when created) should lead with
 * navy (theme accent1 / #00005E). Silver #C9C7C7 is no longer a theme
 * accent but remains the deck's explicit rule gray.
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "Nordea 2024" colorScheme
// ---------------------------------------------------------------------------

export const NORDEA_COLORS = {
  navy: "00005E", // dk2 / accent1 — Nordea signature navy
  brightBlue: "3399FF", // accent2 / folHlink
  lightBlue: "99CCFF", // accent3
  slate: "8B8A8D", // accent4
  peach: "FBD9CA", // accent5
  charcoal: "474748", // accent6
  deepBlue: "0000FF", // lt2

  ink: "000000", // dk1 / hlink
  white: "FFFFFF", // lt1

  green: "00B050", // conventional up (no charts in the seed deck)
  red: "C00000", // conventional down
  paleBlue: "E8F3FF", // table/shading tint from lightBlue

  silver: "C9C7C7", // explicit slide rule gray (not a 2024 theme accent)
  ruleGray: "C9C7C7",
  altRow: "E8F3FF",
} as const;

/**
 * Drop-in ThemeColors override when Nordea style is active. Color
 * slots map 1:1 to the Nordea 2024 colorScheme. Fonts follow the
 * Nordea fontScheme (Arial titles and body).
 */
export const NORDEA_THEME_COLORS: ThemeColors = {
  dark1: NORDEA_COLORS.ink, // #000000 — Text1
  dark2: NORDEA_COLORS.navy, // #00005E — Text2
  light1: NORDEA_COLORS.white, // #FFFFFF — Background1
  light2: NORDEA_COLORS.deepBlue, // #0000FF — Background2
  accent1: NORDEA_COLORS.navy, // #00005E
  accent2: NORDEA_COLORS.brightBlue, // #3399FF
  accent3: NORDEA_COLORS.lightBlue, // #99CCFF
  accent4: NORDEA_COLORS.slate, // #8B8A8D
  accent5: NORDEA_COLORS.peach, // #FBD9CA
  accent6: NORDEA_COLORS.charcoal, // #474748
  hyperlink: NORDEA_COLORS.ink,
  followedHyperlink: NORDEA_COLORS.brightBlue,
  majorFont: "Arial",
  minorFont: "Arial",
};

/**
 * Ordered series palette. Lead with navy (accent1 / dk2), then bright
 * blue, light blue, slate, peach, charcoal.
 */
export const NORDEA_CHART_SERIES_PALETTE = [
  NORDEA_COLORS.navy,
  NORDEA_COLORS.brightBlue,
  NORDEA_COLORS.lightBlue,
  NORDEA_COLORS.slate,
  NORDEA_COLORS.peach,
  NORDEA_COLORS.charcoal,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from master + "Two Content")
// ---------------------------------------------------------------------------

export const NORDEA_SLIDE_DIMENSIONS = { width: 841.9, height: 595.2 } as const;

export const NORDEA_POSITIONS = {
  // Two Content title + dual body columns (layout 27).
  title: { left: 44.7, top: 31.2, width: 497.0, height: 62.5 },
  contentStart: { left: 44.7, top: 115.6 },
  content: { left: 44.7, top: 115.6, width: 753.1, height: 384.2 },
  leftColumn: { left: 44.7, top: 115.6, width: 370.3, height: 384.2 },
  rightColumn: { left: 427.5, top: 115.6, width: 370.3, height: 384.2 },
  pageNumber: { left: 27.8, top: 571.6, width: 9.9, height: 10.6 },
  wordmark: { left: 733.3, top: 553.2, width: 95.3, height: 41.4 },
} as const;

// Authoritative layout names from the Nordea 2024 master (trimmed catalog names).
export const NORDEA_LAYOUT_NAMES = [
  "Title Slide",
  "Title Slide white",
  "Title Slide with 2/3 Pic",
  "Create Layout 1",
  "Create Layout 1 Light Blue Background",
  "Create Layout 1 Light Pink Background",
  "1 up",
  "1 up no heading",
  "1_4 up outline",
  "4 up callout",
  "50/50 vertical",
  "50/50 horizontal",
  "Horizontal MOT",
  "50/50 vertical callout",
  "3 vertical",
  "1_3 vertical",
  "Divider",
  "Divider Blue Background",
  "Title Only",
  "Image and Blue Text Box",
  "2_Image Right",
  "Light Blue Quote",
  "Nordea Blue Quote",
  "1_Title Slide",
  "3 up right",
  "3 up left",
  "Two Content",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Nordea style is active
// ---------------------------------------------------------------------------

export const NORDEA_STYLE_PROMPT = `

## NORDEA BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Nordea.pptx" contains the current Nordea visual standard (2024 Corporate Finance). Match the closest relevant precedent for covers, title-and-content pages, multi-column contents, and section slides. Prefer its title/subtitle hierarchy, bottom-right wordmark, navy-led charts, and bright-blue/slate/peach accents over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Nordea uses:
- Titles / headings / body / bullets / tables / charts / notes / subtitles: \`"Arial"\`
NEVER omit fontFamily when Nordea style is active. If a shape already has the wrong font (including Century Gothic or Calibri from sample content), include fontFamily to correct it to Arial.

### Color palette (from the Nordea master colorScheme "Nordea 2024")
Use ONLY these Nordea brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Ink #${NORDEA_COLORS.ink}
- Text2 / dark2: Navy #${NORDEA_COLORS.navy}
- Background1 / light1: White #${NORDEA_COLORS.white}
- Background2 / light2: Deep blue #${NORDEA_COLORS.deepBlue}
- Accent1: Navy #${NORDEA_COLORS.navy}
- Accent2: Bright blue #${NORDEA_COLORS.brightBlue}
- Accent3: Light blue #${NORDEA_COLORS.lightBlue}
- Accent4: Slate #${NORDEA_COLORS.slate}
- Accent5: Peach #${NORDEA_COLORS.peach}
- Accent6: Charcoal #${NORDEA_COLORS.charcoal}

**Usage:**
- **Primary brand / emphasis**: Navy #${NORDEA_COLORS.navy}
- **Secondary emphasis**: Bright blue #${NORDEA_COLORS.brightBlue} or light blue #${NORDEA_COLORS.lightBlue}
- **Tertiary / highlight**: Slate #${NORDEA_COLORS.slate} or peach #${NORDEA_COLORS.peach}
- **Text / ink**: Navy #${NORDEA_COLORS.navy} or ink #${NORDEA_COLORS.ink} for titles; navy #${NORDEA_COLORS.navy} for body
- **Lines & borders**: Silver #${NORDEA_COLORS.ruleGray} (explicit slide rule gray; not a 2024 theme accent)
- **Background / shading**: Pale blue #${NORDEA_COLORS.altRow} or peach #${NORDEA_COLORS.peach}
- **Up / down**: Green #${NORDEA_COLORS.green} / red #${NORDEA_COLORS.red}
- **Chart series palette** (in order): ${NORDEA_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${NORDEA_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from the Nordea master / "Two Content" layout)
Slide dimensions: 841.9 × 595.2 pt (A4 landscape).

**Placeholder positions:**
- **Title**: left=${NORDEA_POSITIONS.title.left}, top=${NORDEA_POSITIONS.title.top}, width=${NORDEA_POSITIONS.title.width}, height=${NORDEA_POSITIONS.title.height} — Arial, 20pt bold, navy (#${NORDEA_COLORS.navy}), left-aligned.
- **Left body**: left=${NORDEA_POSITIONS.leftColumn.left}, top=${NORDEA_POSITIONS.leftColumn.top}, width=${NORDEA_POSITIONS.leftColumn.width}, height=${NORDEA_POSITIONS.leftColumn.height}
- **Right body**: left=${NORDEA_POSITIONS.rightColumn.left}, top=${NORDEA_POSITIONS.rightColumn.top}, width=${NORDEA_POSITIONS.rightColumn.width}, height=${NORDEA_POSITIONS.rightColumn.height}
- **Content area**: starts at left=${NORDEA_POSITIONS.contentStart.left}, top=${NORDEA_POSITIONS.contentStart.top}, width=${NORDEA_POSITIONS.content.width}, height=${NORDEA_POSITIONS.content.height}
- **Footer / page number**: around left=${NORDEA_POSITIONS.pageNumber.left}, top=${NORDEA_POSITIONS.pageNumber.top}, 8–10pt Arial
- **Wordmark**: bottom-right around left=${NORDEA_POSITIONS.wordmark.left}, top=${NORDEA_POSITIONS.wordmark.top}, width=${NORDEA_POSITIONS.wordmark.width}, height=${NORDEA_POSITIONS.wordmark.height} (master picture "Picture 4" — do not recreate). Do not treat the empty hidden "SecondaryLogo" text box as a logo.

**Content text hierarchy (Arial, navy #${NORDEA_COLORS.navy}):**
- Default body on "Two Content" is ~14pt at the top level and ~10.5pt at lower levels. Effective body color is navy, not charcoal.
- Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use bold Arial or the navy accent; bullets otherwise inherit navy text.

### Charts (Nordea style)
- Font: Arial, minimum 8pt
- **Primary data color**: navy (#${NORDEA_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Nordea style)
- Header row: navy (#${NORDEA_COLORS.navy}) fill with white text, bold
- Body: white fill, charcoal Arial text (10pt)
- Alternate-row shading: pale blue (#${NORDEA_COLORS.altRow})
- Borders: thin silver (#${NORDEA_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Nordea style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin silver (#${NORDEA_COLORS.ruleGray}) or navy (#${NORDEA_COLORS.navy}) rule lines for separation when needed
- Connectors: solid silver (#${NORDEA_COLORS.ruleGray}) lines
- Page numbers / notes: 8-10pt Arial, footer around top=${NORDEA_POSITIONS.pageNumber.top}
- Prefer layout "Two Content" for body slides. "Create Layout 1" is specialized (e.g. Strip profiles), not the generic body. "1 up" adds a separate 11pt heading row; "1 up no heading" uses a 9pt body. Do not default to "Title Slide" or "4 up callout".
- Cover / section pages: "Title Slide", "Title Slide white", "Title Slide with 2/3 Pic", "Divider", "Divider Blue Background", quotes.

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
